import {
  FilterPointsDirectory,
  isLussoNewProgramEnabled
} from '@/infrastructure/Configuration'
import { getQueryParams } from '@/infrastructure/Utils'
import { corporateRoles, companyMemberTypes } from '@/domain/MyAccount'
import { isLussoAdmin as resolveIsLussoAdmin } from '@/domain/adminTypeResolver'
import {
  hasLussoMemberType,
  hasCorporateMemberType,
  hasMemberType,
  normalizeMemberTypes
} from '@/domain/memberTypes'
import { DateFactory } from 'rhg-vue-common'
import { hasR4UAccess } from '@/domain/memberAccess'

const eqCode = left => right => left.code === right.code
const normalizedMemberTypes = normalizeMemberTypes
const shouldUseMemberTypes = customer =>
  isLussoNewProgramEnabled() && normalizedMemberTypes(customer).length > 0
const hasCompanyMemberType = (customer, memberTypes) =>
  hasMemberType(customer, memberTypes)

export default {
  getCurrentCustomer: state => state.currentCustomer,
  isDateRangeSelected: state => state.checkInDate !== null && state.checkOutDate !== null,
  isSomeBrandSelected: state => state.selectedBrands.length !== 0,
  isSomeHotelTypeSelected: state => state.selectedHotelTypes.length !== 0,
  isSomeFilterSelected: (state, getters) =>
    Boolean(
      getters.isSomeBrandSelected ||
        getters.isSomeHotelTypeSelected ||
        getters.isDateRangeSelected ||
        state.isOnlyRewards ||
        state.selectedOfferCountry ||
        state.selectedOfferCity ||
        state.directory.selectedCountry ||
        state.directory.points.min !== FilterPointsDirectory.MIN ||
        state.directory.points.max !== FilterPointsDirectory.MAX
    ),
  isSelectedBrand: state => brandCode =>
    state.selectedBrands.some(brand => brand.code === brandCode),
  isSelectedHotelType: state => hotelTypeCode =>
    state.selectedHotelTypes.some(hotelType => hotelType.code === hotelTypeCode),
  isSelectedHotelTypes: state => hotel =>
    state.selectedHotelTypes.every(hotelType =>
      hotel.types ? hotel.types.includes(hotelType.code) : hotel.type === hotelType.code
    ),
  hotels: (state, getters) => {
    let hotels = state.hotels
    if (getters.isSomeBrandSelected) {
      hotels = hotels.filter(hotel => getters.isSelectedBrand(hotel.brand))
    }
    if (getters.isSomeHotelTypeSelected) {
      hotels = hotels.filter(hotel => getters.isSelectedHotelTypes(hotel))
    }
    return hotels
  },
  tripAdvisorRatingForHotel: state => code =>
    state.tripAdvisorRating.find(rating => rating.code === code),

  brands: state => {
    const brandCodes = state.hotels
      .map(hotel => hotel.brand)
      .concat(state.offers.reduce((acc, offer) => acc.concat(offer.brands), []))
    return state.brands.filter(brand => brandCodes.some(brandCode => brand.code === brandCode))
  },
  hotelTypes: state => {
    const hotelTypes = Array.from(
      new Set(
        state.hotels
          .reduce((acc, hotel) => acc.concat(hotel.types || hotel.type), [])
          .filter(type => type)
      )
    )
    return state.hotelTypes.filter(hotelType =>
      hotelTypes.some(hotelTypeCode => hotelType.code === hotelTypeCode)
    )
  },
  offers: (state, getters) => {
    let offers = state.offers

    if (getters.isSomeBrandSelected) {
      offers = offers.filter(offer =>
        offer.brands.some(brandCode => getters.isSelectedBrand(brandCode))
      )
    }

    if (getters.isSomeHotelTypeSelected) {
      offers = offers.filter(offer =>
        offer.categories.some(hotelTypeCode => getters.isSelectedHotelType(hotelTypeCode))
      )
    }

    if (state.isOnlyRewards) {
      offers = offers.filter(offer => offer.rewards)
    }

    if (state.selectedOfferCountry) {
      offers = offers.filter(offer => offer.countries.some(eqCode(state.selectedOfferCountry)))
    }

    if (getters.isDateRangeSelected) {
      offers = offers.filter(
        offer =>
          offer.bookingStartDate < state.checkOutDate && offer.bookingEndDate > state.checkInDate
      )
    }

    if (state.selectedOfferCity) {
      offers = offers.filter(offer =>
        offer.countries
          .filter(eqCode(state.selectedOfferCountry))
          .some(country => country.cities.some(eqCode(state.selectedOfferCity)))
      )
    }

    return offers.sort((offerA, offerB) => offerA.priority - offerB.priority)
  },
  directoryHotels: state => {
    return state.directory.hotels
  },
  directoryCountries: state => {
    return state.directory.countries
  },
  directoryCities: state => {
    return state.directory.cities
  },
  directoryStates: state => {
    return state.directory.states
  },
  directoryTotalResults: state => {
    if (state.directory.pagination.total === null) {
      return '0'
    } else {
      return state.directory.pagination.total
    }
  },

  checkInDate: state => state.checkInDate && DateFactory.formatDate(state.checkInDate),

  checkOutDate: state => state.checkOutDate && DateFactory.formatDate(state.checkOutDate),

  getUrlParams: () => () => getQueryParams(window.location.search),

  userCountry: () => {
    const country =
      global.AkamaiService && global.AkamaiService.userCountry
        ? global.AkamaiService.userCountry()
        : 'ES'
    return country || 'ES'
  },

  customerProfile: state => state.customerProfile,
  loadingCustomer: state => state.loadingCustomer,
  preferences: state => state.preferences,
  loadingPreferences: state => state.loadingPreferences,
  loadingNewsletter: state => state.loadingNewsletter,
  getCustomerSummary: state => state.customerSummary,
  currentModalId: state => state.currentModalId,
  isFormEdited: state => state.isFormEdited,
  isChangeDiscarded: state => state.isChangeDiscarded,
  isNewCorporateProgramActive: state => {
    const customer = state.currentCustomer

    if (!customer || Object.keys(customer).length === 0) return false

    const memberTypes = Array.isArray(customer.memberTypes)
      ? customer.memberTypes
      : customer.memberType?.split('|').map(type => type.trim()).filter(Boolean) ?? []

    return (
      customer.programType === 'CORP' ||
        hasCorporateMemberType(customer) ||
        memberTypes.some(type => Object.values(corporateRoles).includes(type))
    )
  },
  isCorporateBooker: (state, getters) => {
    const customer = state.currentCustomer
    if (shouldUseMemberTypes(customer)) {
      return hasCompanyMemberType(customer, companyMemberTypes.CORPORATE)
    }

    return (
      getters.isNewCorporateProgramActive &&
      customer?.memberType?.toLowerCase()?.includes(corporateRoles.BOOKER)
    )
  },
  isCorporateAdminOrDeputy: (state, getters) => {
    const customer = state.currentCustomer
    if (shouldUseMemberTypes(customer)) {
      return hasCompanyMemberType(customer, companyMemberTypes.CORPORATE)
    }

    return (
      getters.isNewCorporateProgramActive &&
      [corporateRoles.ADMINISTRATOR, corporateRoles.DEPUTY].some(role =>
        customer?.memberType?.toLowerCase()?.includes(role)
      )
    )
  },
  isR4UAdmin: state => {
    const customer = state.currentCustomer
    if (shouldUseMemberTypes(customer)) {
      return hasCompanyMemberType(customer, companyMemberTypes.R4U)
    }

    const memberType = customer?.memberType?.toLowerCase()
    return [corporateRoles.TMC_ADMIN, corporateRoles.PCR_ADMIN].some(role =>
      memberType?.includes(role)
    )
  },
  isR4UMember: state => {
    const customer = state.currentCustomer
    return hasR4UAccess(customer)
  },
  isLussoMember: state =>
    isLussoNewProgramEnabled() &&
    hasLussoMemberType(state.currentCustomer),
  isPureLussoMember: (state, getters) => {
    if (!getters.isLussoMember) return false

    const memberTypes = normalizedMemberTypes(state.currentCustomer)

    return (
      memberTypes.length > 0 &&
      memberTypes.every(memberType => companyMemberTypes.LUSSO.includes(memberType))
    )
  },
  isLussoAdmin: state =>
    resolveIsLussoAdmin(state.currentCustomer, isLussoNewProgramEnabled()),
  isReservationFilterVisible: (state, getters) =>
    (getters.isCorporateAdminOrDeputy || getters.isLussoMember ||
      (getters.isCorporateBooker && state.reservations.memberType !== 'personal')) &&
    state.reservations.groupType === 'future',
  multiFactor: state =>
    Object.keys(state.customerProfile).length !== 0 && state.customerProfile.multifactor,
  telephone: state =>
    Object.keys(state.customerProfile).length !== 0 && state.customerProfile.telephone,
  transcationsFilterApplied: state => state.transactions?.filterApplied,
  isFilterSelectedClear: state =>
    Object.values(state.transactions?.filters).every(val => val === ''),
  loadingClaimPoints: state => state.loadingClaimPoints
}
